import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Download,
  Refresh,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import spopRevenueService from '../services/spopRevenueService';
import { format } from 'date-fns';
import { useAuth } from '../hooks/useAuth';

const SPOPRevenue: React.FC = () => {
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [isExporting, setIsExporting] = useState(false);
  
  const { user } = useAuth();
  const isOperator = user?.role === 'operator';
  const operatorSpopId = isOperator && user?.spop_id ? user.spop_id : undefined;

  // Query untuk data revenue SPOP
  const { 
    data: revenueData, 
    isLoading, 
    error, 
    refetch 
  } = useQuery({
    queryKey: ['spop-revenue', selectedMonth, selectedYear, operatorSpopId],
    queryFn: async () => {
      return await spopRevenueService.getSPOPRevenue({
        month: selectedMonth.toString(),
        year: selectedYear.toString(),
        ...(operatorSpopId ? { spop_id: operatorSpopId } : {}),
      });
    },
  });

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  const handleRefresh = () => {
    refetch();
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      await spopRevenueService.exportSPOPRevenue({
        month: selectedMonth.toString(),
        year: selectedYear.toString(),
        ...(operatorSpopId ? { spop_id: operatorSpopId } : {}),
      });
    } catch (error) {
      console.error('Export error:', error);
      alert('Terjadi kesalahan saat mengekspor data: ' + (error instanceof Error ? error.message : 'Unknown error'));
    } finally {
      setIsExporting(false);
    }
  };

  // Generate year options (current year and previous 5 years)
  const yearOptions = [];
  const currentYear = new Date().getFullYear();
  for (let i = 0; i < 6; i++) {
    yearOptions.push(currentYear - i);
  }

  // Month options
  const monthOptions = [
    { value: 1, label: 'Januari' },
    { value: 2, label: 'Februari' },
    { value: 3, label: 'Maret' },
    { value: 4, label: 'April' },
    { value: 5, label: 'Mei' },
    { value: 6, label: 'Juni' },
    { value: 7, label: 'Juli' },
    { value: 8, label: 'Agustus' },
    { value: 9, label: 'September' },
    { value: 10, label: 'Oktober' },
    { value: 11, label: 'November' },
    { value: 12, label: 'Desember' },
  ];

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">
          Error loading SPOP revenue data: {error instanceof Error ? error.message : 'Unknown error'}
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" component="h1">
            {isOperator ? 'Rekap Pendapatan SPOP Saya' : 'Rekap Pendapatan SPOP'}
          </Typography>
          {isOperator && user?.spop && (
            <Typography variant="subtitle1" color="primary.main" sx={{ mt: 0.5 }}>
              SPOP: {user.spop.name} ({user.spop.areaCode})
            </Typography>
          )}
          <Typography variant="subtitle1" color="text.secondary" sx={{ mt: 0.5 }}>
            {monthOptions.find(m => m.value === selectedMonth)?.label} {selectedYear}
          </Typography>
        </Box>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={handleExport}
            color="success"
            disabled={isExporting || isLoading}
          >
            {isExporting ? 'Mengekspor...' : 'Export Excel'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={handleRefresh}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Filter Controls */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" gap={2} alignItems="center">
            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel>Bulan</InputLabel>
              <Select
                value={selectedMonth}
                label="Bulan"
                onChange={(e) => setSelectedMonth(Number(e.target.value))}
              >
                {monthOptions.map((month) => (
                  <MenuItem key={month.value} value={month.value}>
                    {month.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel>Tahun</InputLabel>
              <Select
                value={selectedYear}
                label="Tahun"
                onChange={(e) => setSelectedYear(Number(e.target.value))}
              >
                {yearOptions.map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      {revenueData?.data?.summary && (
        <Box display="grid" gridTemplateColumns="repeat(auto-fit, minmax(250px, 1fr))" gap={2} mb={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="success.main">
                Total Lunas
              </Typography>
              <Typography variant="h4">
                {formatCurrency(revenueData.data.summary.total_paid_amount)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {revenueData.data.summary.total_paid_bills} tagihan
              </Typography>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent>
              <Typography variant="h6" color="warning.main">
                Total Menunggu
              </Typography>
              <Typography variant="h4">
                {formatCurrency(revenueData.data.summary.total_pending_amount)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {revenueData.data.summary.total_pending_bills} tagihan
              </Typography>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent>
              <Typography variant="h6" color="error.main">
                Total Terlambat
              </Typography>
              <Typography variant="h4">
                {formatCurrency(revenueData.data.summary.total_overdue_amount)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {revenueData.data.summary.total_overdue_bills} tagihan
              </Typography>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent>
              <Typography variant="h6" color="primary.main">
                Total Keseluruhan
              </Typography>
              <Typography variant="h4">
                {formatCurrency(revenueData.data.summary.total_amount)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {revenueData.data.summary.total_bills} tagihan
              </Typography>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* SPOP Revenue Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {isOperator ? 'Status Tagihan SPOP Anda' : 'Pendapatan per SPOP'}
          </Typography>
          
          {isLoading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>SPOP</TableCell>
                    <TableCell align="center">Bill Lunas</TableCell>
                    <TableCell align="center">Bill Menunggu</TableCell>
                    <TableCell align="center">Bill Terlambat</TableCell>
                    <TableCell align="right">Amount Lunas</TableCell>
                    <TableCell align="right">Amount Menunggu</TableCell>
                    <TableCell align="right">Amount Terlambat</TableCell>
                    <TableCell align="right">Total Amount</TableCell>
                    <TableCell align="center">Collection Rate</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {revenueData?.data?.spops && Array.isArray(revenueData.data.spops) ? 
                    revenueData.data.spops.map((spop: any) => {
                      const collectionRate = spop.total_amount > 0 
                        ? ((spop.paid_amount / spop.total_amount) * 100).toFixed(1)
                        : '0.0';
                      
                      return (
                        <TableRow key={spop.spop_id || 'no-spop'}>
                          <TableCell>
                            <Box>
                              <Typography variant="subtitle2" fontWeight="bold">
                                {spop.spop_name || 'Tanpa SPOP'}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {spop.area_code || '-'}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            <Chip 
                              label={spop.paid_bills || 0} 
                              color="success" 
                              size="small" 
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Chip 
                              label={spop.pending_bills || 0} 
                              color="warning" 
                              size="small" 
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Chip 
                              label={spop.overdue_bills || 0} 
                              color="error" 
                              size="small" 
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" color="success.main">
                              {formatCurrency(spop.paid_amount)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" color="warning.main">
                              {formatCurrency(spop.pending_amount)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" color="error.main">
                              {formatCurrency(spop.overdue_amount)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="subtitle2" fontWeight="bold">
                              {formatCurrency(spop.total_amount)}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Chip 
                              label={`${collectionRate}%`}
                              color={parseFloat(collectionRate) >= 80 ? 'success' : parseFloat(collectionRate) >= 60 ? 'warning' : 'error'}
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      );
                    }) : (
                    <TableRow>
                      <TableCell colSpan={9} align="center">
                        Tidak ada data pendapatan SPOP
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default SPOPRevenue;
